/**
 * The sheet stack as a breadcrumb (§6.1): "Kofi A. / GHS 250.00 withdrawal /
 * Refund GHS 252.50". Every crumb but the last pops back to it. On phones only
 * the current record shows; the Back button names the one before it.
 */

import { Fragment } from 'react';
import { cn } from '@/lib/utils';

interface SheetBreadcrumbItem {
  /** `type:id` of the sheet. */
  key: string;
  /** The sheet's title (the human fact). */
  label: string;
}

export interface SheetBreadcrumbProps {
  items: SheetBreadcrumbItem[];
  /** Pops back so the crumb at `index` is the top sheet. */
  onSelect: (index: number) => void;
  className?: string;
}

export function SheetBreadcrumb({
  items,
  onSelect,
  className,
}: Readonly<SheetBreadcrumbProps>) {
  const lastIndex = items.length - 1;
  return (
    <nav aria-label="Open records" className={cn('min-w-0', className)}>
      <ol className="flex min-w-0 items-center gap-1.5 text-sm">
        {items.map((item, index) => {
          const isLast = index === lastIndex;
          return (
            <Fragment key={item.key}>
              {index > 0 ? (
                <li
                  aria-hidden="true"
                  className="hidden shrink-0 text-subtle-foreground md:block"
                >
                  /
                </li>
              ) : null}
              <li
                className={cn(
                  'min-w-0',
                  isLast ? 'flex-1' : 'hidden shrink md:block',
                )}
              >
                {isLast ? (
                  <span
                    aria-current="page"
                    className="block truncate text-base font-semibold text-foreground md:text-sm md:font-medium"
                  >
                    {item.label}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => onSelect(index)}
                    className="block max-w-36 truncate rounded-xs text-muted-foreground transition-colors duration-(--dur-fast) hover:text-foreground"
                  >
                    {item.label}
                  </button>
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
